import React, { createContext, useContext, useId, useMemo, useState } from "react";

/** Small, dependency-free UI kit to replace `@/components/ui/*` imports.
 *  Exports:
 *  - Tabs, TabsList, TabsTrigger, TabsContent
 *  - Card, CardHeader, CardTitle, CardContent
 *  - Input, Textarea, Label
 *  - Button
 */

function cn(...parts: Array<string | undefined | false | null>) {
  return parts.filter(Boolean).join(" ");
}

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
};
export const Button: React.FC<ButtonProps> = ({ className, variant = "default", size = "md", ...props }) => {
  const variantCls =
    variant === "secondary"
      ? "bg-transparent border border-gray-300 hover:bg-gray-50"
      : variant === "ghost"
      ? "bg-transparent hover:bg-gray-100"
      : "bg-black text-white hover:opacity-90";
  const sizeCls = size === "sm" ? "px-2 py-1 text-sm" : size === "lg" ? "px-4 py-2" : "px-3 py-2";
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        variantCls,
        sizeCls,
        className
      )}
      {...props}
    />
  );
};

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
export const Input: React.FC<InputProps> = ({ className, ...props }) => (
  <input
    className={cn(
      "w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-gray-300",
      className
    )}
    {...props}
  />
);

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
export const Textarea: React.FC<TextareaProps> = ({ className, rows = 3, ...props }) => (
  <textarea
    className={cn(
      "w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-gray-300",
      className
    )}
    rows={rows}
    {...props}
  />
);

export type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;
export const Label: React.FC<LabelProps> = ({ className, ...props }) => (
  <label className={cn("block text-sm font-medium text-gray-800", className)} {...props} />
);

export const Card: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("bg-white border border-gray-200 rounded-2xl shadow-sm", className)} {...props} />
);
export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("p-4 border-b border-gray-100", className)} {...props} />
);
export const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ className, ...props }) => (
  <h3 className={cn("text-lg font-semibold", className)} {...props} />
);
export const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("p-4", className)} {...props} />
);

interface TabsContextValue {
  value: string;
  setValue: (v: string) => void;
}
const TabsContext = createContext<TabsContextValue | null>(null);
function useTabsCtx(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("Tabs components must be used within <Tabs>");
  return ctx;
}

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultValue: string;
  value?: string;
  onValueChange?: (v: string) => void;
}
export const Tabs: React.FC<TabsProps> = ({ defaultValue, value: controlled, onValueChange, className, children, ...rest }) => {
  const [uncontrolled, setUncontrolled] = useState(defaultValue);
  const value = controlled ?? uncontrolled;
  const setValue = (v: string) => {
    if (controlled === undefined) setUncontrolled(v);
    onValueChange?.(v);
  };
  const ctx = useMemo(() => ({ value, setValue }), [value]);
  return (
    <TabsContext.Provider value={ctx}>
      <div className={cn("w-full", className)} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

export const TabsList: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("flex gap-2 flex-wrap", className)} {...props} />
);

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}
export const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, className, children, ...props }) => {
  const { value: active, setValue } = useTabsCtx();
  const selected = active === value;
  return (
    <button
      type="button"
      data-state={selected ? "active" : "inactive"}
      onClick={() => setValue(value)}
      className={cn(
        "px-3 py-1.5 rounded-full border text-sm",
        selected ? "bg-black text-white border-black" : "bg-white hover:bg-gray-50 border-gray-300",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}
export const TabsContent: React.FC<TabsContentProps> = ({ value, className, children, ...props }) => {
  const { value: active } = useTabsCtx();
  const hidden = active !== value;
  const id = useId();
  return (
    <div
      id={`tabs-content-${id}`}
      role="tabpanel"
      aria-hidden={hidden}
      hidden={hidden}
      className={cn("mt-3", className)}
      {...props}
    >
      {children}
    </div>
  );
};
